import { memo, useEffect, useMemo, useRef } from "react";
import {
  buildBeatGrid,
  getActiveChordAtTime,
  getChordChangeAtBeat,
  getChordLabel,
  normalizeChordChanges,
} from "@/music/chords/chordSyncUtils";

const formatTime = (seconds = 0) => {
  const safe = Math.max(0, Number(seconds) || 0);
  return `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, "0")}`;
};

const ChordBeatCarousel = memo(({
  events = [],
  bpm = 120,
  duration = 0,
  currentTime = 0,
  beatsPerBar = 4,
  subdivision = 1,
  mode = "practice",
  selectedBeatIndex = null,
  selectedChangeId = null,
  onSeek,
  onAddChordChange,
  onUpdateChordChange,
  onRemoveChordChange,
  onSelectBeat,
  snapToBeat = true,
  autoScroll = true,
  zoom = 1,
}) => {
  const beatRefs = useRef([]);
  const changes = useMemo(() => normalizeChordChanges(events, duration), [duration, events]);
  const gridDuration = Math.max(Number(duration) || 0, changes[changes.length - 1]?.end || 0, 4);
  const beatCells = useMemo(
    () => buildBeatGrid({ duration: gridDuration, bpm, beatsPerBar, subdivision }),
    [beatsPerBar, bpm, gridDuration, subdivision]
  );
  const matchedBeatIndex = beatCells.findIndex((beat) => currentTime >= beat.start && currentTime < beat.end);
  const activeBeatIndex = matchedBeatIndex >= 0
    ? matchedBeatIndex
    : currentTime >= beatCells[beatCells.length - 1]?.end
      ? beatCells.length - 1
      : 0;
  const activeChord = getActiveChordAtTime(changes, currentTime);
  const isEdit = mode === "edit";
  const cellWidth = Math.round(Math.max(72, (isEdit ? 92 : 84) * Number(zoom || 1)));

  useEffect(() => {
    if (!autoScroll || activeBeatIndex < 0) return;
    beatRefs.current[activeBeatIndex]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeBeatIndex, autoScroll]);

  return (
    <div data-snap-to-beat={isEdit && snapToBeat ? "true" : undefined} className="steel-scroll-soft overflow-x-auto rounded-2xl border border-white/10 bg-[#090d16]/80 p-3 sm:p-4">
      <div className="flex min-w-max">
        {beatCells.map((beat) => {
          const change = getChordChangeAtBeat(changes, beat);
          const changeIndex = change ? changes.findIndex((event) => event.id === change.id) : -1;
          const sectionChanged = Boolean(change?.section && (changeIndex === 0 || change.section !== changes[changeIndex - 1]?.section));
          const label = change ? getChordLabel(change) : "";
          const playingHere = beat.index === activeBeatIndex;
          const holdingActiveChord = Boolean(change && change.id === activeChord?.id);
          const selected = beat.index === selectedBeatIndex || Boolean(change && change.id === selectedChangeId);
          const playhead = playingHere
            ? Math.max(0, Math.min(100, ((Number(currentTime) - beat.start) / beat.duration) * 100))
            : 0;
          return (
            <button
              key={beat.index}
              ref={(node) => { beatRefs.current[beat.index] = node; }}
              type="button"
              onClick={() => {
                onSelectBeat?.(beat, change);
                onSeek?.(beat.start);
              }}
              onDoubleClick={isEdit ? () => {
                if (change) onUpdateChordChange?.(change, { focusSelector: true });
                else onAddChordChange?.(beat);
              } : undefined}
              style={{ width: `${cellWidth}px` }}
              className={`relative flex h-32 shrink-0 flex-col justify-end overflow-hidden border-r border-white/10 px-3 pb-3 pt-8 text-left transition sm:h-36 ${
                sectionChanged
                  ? "border-l-2 border-l-cyan-200/65"
                  : beat.isBarStart
                    ? "border-l-2 border-l-violet-300/35"
                    : ""
              } ${
                playingHere
                  ? "bg-cyan-300/10"
                  : selected
                    ? "bg-violet-400/12"
                    : "bg-white/[0.018] hover:bg-white/[0.05]"
              }`}
              aria-current={playingHere ? "true" : undefined}
            >
              <span className="absolute left-2 top-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {beat.isBarStart ? `Bar ${beat.barIndex + 1}` : beat.beatInBar}
              </span>
              {sectionChanged && (
                <span className="absolute right-2 top-2 max-w-[58%] truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-200">
                  {change.section}
                </span>
              )}
              {label && (
                <span className={`text-2xl font-black tracking-tight sm:text-3xl ${holdingActiveChord ? "text-cyan-100 drop-shadow-[0_0_10px_rgba(103,232,249,.65)]" : "text-white"}`}>
                  {label}
                </span>
              )}
              <span className="mt-2 text-[11px] font-medium text-slate-500">{formatTime(beat.start)}</span>
              {playingHere && (
                <span
                  className="pointer-events-none absolute inset-y-0 z-20 w-[2px] bg-cyan-100 shadow-[0_0_16px_rgba(103,232,249,.95)]"
                  style={{ left: `${playhead}%` }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});

ChordBeatCarousel.displayName = "ChordBeatCarousel";

export default ChordBeatCarousel;
